import { useEffect, useState } from "react";
import Axios from "axios";

import Config from "../components/Config";
import { trackingInterval } from "../utils/orders";

/**
 * Interroge GET /customer/orders/:id/tracking, comme useOrderTracking sur
 * mobile :
 * - 10 s pendant une étape active, 30 s quand rien ne bouge, 120 s après
 *   une erreur, arrêt une fois l'étape terminée (voir trackingInterval) ;
 * - en pause tant que l'onglet est caché, relancé dès qu'il revient ;
 * - If-None-Match quand le serveur a exposé un ETag : un 304 garde la
 *   réponse précédente telle quelle.
 *
 * Renvoie { data, failed, diag } : `data` est la dernière réponse valable,
 * conservée même si l'appel suivant échoue ; `diag` décrit le dernier appel,
 * pour la ligne de diagnostic de la carte statut.
 */
const EMPTY = { data: undefined, failed: false, diag: null };

export default function useOrderTracking(orderId, enabled, authUser) {
  const [state, setState] = useState(EMPTY);
  const uid = authUser?.user_uid;
  const client = authUser?.user_client;
  const token = authUser?.user_access_token;

  useEffect(() => {
    setState(EMPTY);
    if (!enabled || !orderId) return undefined;

    let cancelled = false;
    let timer = null;
    let etag = null;
    let last;
    let failed = false;

    const schedule = () => {
      clearTimeout(timer);
      const ms = trackingInterval({ failed, data: last });
      if (ms !== null && !document.hidden) timer = setTimeout(poll, ms);
    };

    async function poll() {
      const at = new Date().toISOString();
      try {
        const response = await Axios({
          url: `${Config.apiURL}/customer/orders/${orderId}/tracking`,
          method: "GET",
          headers: {
            uid,
            client,
            "access-token": token,
            ...(etag && last ? { "If-None-Match": etag } : {}),
          },
          validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
        });
        if (cancelled) return;

        if (response.status !== 304) {
          etag = response.headers?.etag || null;
          last = response.data;
        }
        failed = false;
        setState({
          data: last,
          failed: false,
          diag: {
            at,
            ok: true,
            http: response.status,
            state: last?.state,
            leg: last?.leg ?? null,
            stops: last?.stops_before_user ?? null,
            driver: Boolean(last?.driver?.name),
          },
        });
      } catch (error) {
        if (cancelled) return;
        failed = true;
        setState((previous) => ({
          data: previous.data,
          failed: true,
          diag: { at, ok: false, http: error?.response?.status ?? null },
        }));
      }
      schedule();
    }

    const onVisibility = () => {
      clearTimeout(timer);
      if (!document.hidden && last?.state !== "completed") poll();
    };

    poll();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [orderId, enabled, uid, client, token]);

  return state;
}
